<template>
  <v-container class="px-0" fluid>
    <TimePairDialog
      v-if="actionable"
      :value="timePairEditDialog"
      :timePair="editingTimePair"
      :shiftTimePair="editShiftTimePair"
      @close="closeEditTimePair"
    />

    <TimePairDialog
      v-if="actionable"
      :value="segmentTimePairAddingDialog"
      :shiftTimePair="shiftTimePair"
      @close="closeAddSegmentTimePair"
    />

    <TimePairDetailsDialog
      :value="timePairViewDialog"
      :timePair="viewingTimePair"
      @close="closeViewTimePair"
    />

    <TimePairMapDialog
      :value="timePairMapDialogActive"
      :timePair="viewingTimePair"
      @close="timePairMapDialogActive = false"
    />

    <SegmentTimePairTableDialog
      v-if="isMobile()"
      :value="segmentTimePairViewDialog"
      :headers="segmentTimeTableHeaders"
      :items="viewingSegmentTimePairs"
      @close="closeViewSegmentTimePairs"
    >
      <template
        v-if="isSegmentTimePairFeatureActive
              && (viewingSegmentTimePairsShift.employeeAssignment
                  && viewingSegmentTimePairsShift.employeeAssignment.active)"
        v-slot:subtitle-actions
      >
        <v-btn
          v-if="isTimePairAddFeatureActive"
          v-show="actionable && !viewingSegmentTimePairsShift.isInactive"
          color="primary"
          class="mr-2"
          @click="addSegmentTimePair(viewingSegmentTimePairsShift)"
        >
          {{ $t('actions.addBreak') }}
          <v-icon right>add</v-icon>
        </v-btn>
      </template>
      <template
        v-if="isSegmentTimePairFeatureActive && actionable
              && (viewingSegmentTimePairsShift.employeeAssignment
                  && viewingSegmentTimePairsShift.employeeAssignment.active)"
        v-slot:actions="{ item }"
      >
        <div v-if="!item.isInactive && isTimePairEditFeatureActive && item.pairType === 'BREAK'">
          <v-icon
            :class="actionIcon"
            @click="editTimePair(item, viewingSegmentTimePairsShift)"
          >
            edit
          </v-icon>
          <v-icon
            :class="actionIcon"
            @click="removeTimePair(item, viewingSegmentTimePairsShift)"
          >
            delete
          </v-icon>
        </div>
        <div v-else class="font-italic text--disabled">
          {{ $t('misc.unavailable') }}
        </div>
      </template>
    </SegmentTimePairTableDialog>

    <v-data-table
      :headers="activeHeaders"
      :items="getTimePairsOrAdjustedTimePairs()"
      :items-per-page="itemsPerPage"
      :loading="timePairsLoading"
      :custom-sort="customPayCycleTableSort"
      :no-data-text="`${$t('tables.noData')}`"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :mobile-breakpoint="$vuetify.breakpoint.mobileBreakpoint"
      :item-class="getItemClass"
      show-expand
      disable-pagination
      hide-default-footer
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title :class="{ 'font-weight-bold': isMobile() }">
            {{ header }}
          </v-toolbar-title>
          <v-divider
            inset
            vertical
            class="mx-4"
          />
          <v-spacer/>
          {{ $t('tableHeaders.totalTime', { duration: duration }) }}
        </v-toolbar>
        <v-toolbar flat>
          <v-switch
            v-if="roundingEnabled"
            v-model="viewAdjustedTime"
            color="secondary"
            :label="`${$t('titles.adjustedTime')}`"
            @click="updateTableHeaders()"
          />
        </v-toolbar>
      </template>

      <template v-slot:header.clone="{ header }">
        <th :class="header.class">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on" class="text-no-wrap">{{ header.text }}</span>
            </template>
            <span>
            {{ $t('indicators.clone.message') }}
            <br/>
            {{ $t('indicators.clone.notAvailable') }}
          </span>
          </v-tooltip>
        </th>
      </template>

      <template v-slot:item.data-table-expand="{ expand, item, isExpanded }">
        <template v-if="!item.isAbsence">
          {{
            (isExpanded && item.segmentTimePairs.length === 0 && item.startingWorkScheduleDay == null)
              ? expand(false)
              : null
          }}
          <span v-if="isMobile()"
                class="grey--text"> {{ isExpanded ? 'Hide' : 'Show' }} Additional Time Data </span>
          <v-tooltip top :disabled="isMobile()">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                icon
                :disabled="(isMobile() || item.segmentTimePairs.length === 0)
                           && item.startingWorkScheduleDay == null"
                @click="expand(!isExpanded)"
              >
                <v-icon>
                  {{ isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
                </v-icon>

              </v-btn>
            </template>
            <span> {{ isExpanded ? $t('indicators.hideAdditionalData') : $t('indicators.showAdditionalData') }} </span>
          </v-tooltip>
        </template>
      </template>
      <template v-slot:item.info="{ item }">
        <TimePairStatusIndicator :items="getRowIndicators(item)"/>
      </template>
      <template v-slot:item.employeeId="{ item }">
        {{ item.employeeAssignment.id }}
      </template>
      <template v-slot:item.position="{ item }">
        {{ item.employeeAssignment.position.description ? item.employeeAssignment.position.description
        : `(${item.employeeAssignment.position.id})` }}
      </template>
      <template v-slot:item.startDate="{ item }">
        <StartDateTableCell :item="item"/>
      </template>
      <template v-slot:item.clockIn="{ item }">
        <StartTimeTableCell :item="item"/>
      </template>
      <template v-slot:item.clockOut="{ item }">
        <EndTimeTableCell :item="item" :wrap="isMenuDrawerClosable()" badge/>
      </template>
      <template v-slot:item.absenceType="{ item }">
        <span> {{ item.absenceType }} </span>
      </template>
      <template v-slot:item.duration="{ item }">
        <v-tooltip bottom :disabled="!showTimePairDeviation(item, false)">
          <template v-slot:activator="{ on }">
            <v-badge
              dot
              :value="showTimePairDeviation(item, false)"
              color="var(--v-warning-lighten1)"
            >
              <div v-on="on">
                {{ formatDuration(item) }}
              </div>
            </v-badge>
          </template>
          {{ getWorkScheduleDeviation(item) }}
        </v-tooltip>
      </template>
      <template v-slot:item.expectedDuration="{ item }">
        {{ getExpectedDuration(item) }}
      </template>
      <template v-slot:item.status="{ item }">
        <ApprovalStatusTableCell v-if="!item.isAbsence" :item="item"/>
        <span v-else>{{ item.absenceType }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip v-if="!item.isAbsence && !isMobile()" top>
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isSegmentTimePairFeatureActive && isTimePairAddFeatureActive
                    && item.employeeAssignment.active"
              v-show="actionable && !item.isInactive"
              v-on="on"
              :class="actionIcon"
              @click="addSegmentTimePair(item)"
            >
              add
            </v-icon>
          </template>
          <span>{{ $t('actions.addBreak') }}</span>
        </v-tooltip>
        <v-icon
          v-if="isMobile() && !item.isAbsence && isSegmentTimePairFeatureActive && isTimePairAddFeatureActive
                    && item.employeeAssignment.active && item.segmentTimePairs.length === 0"
          :class="actionIcon"
          @click="addSegmentTimePair(item)"
        >
          add
        </v-icon>
        <v-icon
          v-if="isMobile() && item.segmentTimePairs.length > 0"
          :class="actionIcon"
          @click="viewSegmentTimePairs(item)"
        >
          emoji_food_beverage
        </v-icon>
        <v-tooltip v-if="!isMobile() && isLocationFeatureActive" top>
          <template v-slot:activator="{ on }">
            <v-icon
              v-on="on"
              :disabled="!hasLocationData(item)"
              :class="actionIcon"
              @click="viewTimePairMap(item)"
            >
              {{ hasLocationData(item) ? 'location_on' : 'location_off' }}
            </v-icon>
          </template>
          <span>{{$t('indicators.showGeoData')}}</span>
        </v-tooltip>
        <v-tooltip top :disabled="isMobile()">
          <template v-slot:activator="{ on }">
            <v-icon
              v-on="on"
              :class="actionIcon"
              @click="viewTimePair(item)"
            >
              visibility
            </v-icon>
          </template>
          <span> {{ $t('indicators.showDetails')}}</span>
        </v-tooltip>
        <v-tooltip v-if="!item.isAbsence" top :disabled="isMobile()">
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isTimePairEditFeatureActive && item.employeeAssignment.active"
              v-show="actionable && !item.isInactive"
              v-on="on"
              :class="actionIcon"
              @click="editTimePair(item)"
            >
              edit
            </v-icon>
          </template>
          <span> {{ $t('actions.edit')}}</span>
        </v-tooltip>
        <v-tooltip
          v-if="!item.isAbsence && isDeleteTimePairActive"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isTimePairEditFeatureActive && item.employeeAssignment.active"
              v-show="actionable && !item.isInactive"
              v-on="on"
              :class="actionIcon"
              @click="removeTimePair(item)"
            >
              delete
            </v-icon>
          </template>
          <span> {{ $t('actions.delete')}}</span>
        </v-tooltip>
      </template>
      <template
        v-if="isCloneTimeActive"
        v-slot:item.clone="{ item }"
      >
        <v-tooltip v-if="!item.isAbsence" top :disabled="isMobile()">
          <template v-slot:activator="{ on }">
            <v-icon
              v-on="on"
              :class="actionIcon"
              :disabled="!!activeTimePair.id"
              @click="cloneShift(item)"
            >
              play_arrow
            </v-icon>
          </template>
          <span> {{ $t('clock.start')}}</span>
        </v-tooltip>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td v-if="!item.isAbsence" :colspan="headers.length" class="pb-4">
          <v-card
            v-if="!isMobile() && item.segmentTimePairs.length !== 0"
            flat
            :class="{ 'mb-2': item.startingWorkScheduleDay != null }"
          >
            <SegmentTimePairTable
              :headers="segmentTimeTableHeaders"
              :items="item.segmentTimePairs"
            >
              <template v-slot:actions="{ item: segmentTimePair }">
                <div
                  v-if="isSegmentTimePairFeatureActive && isTimePairEditFeatureActive && actionable
                        && item.employeeAssignment.active && !segmentTimePair.isInactive"
                >
                  <v-tooltip
                    v-if="segmentTimePair.pairType === 'BREAK'"
                    top :disabled="isMobile()"
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        v-on="on"
                        :class="actionIcon"
                        @click="editTimePair(segmentTimePair,item)"
                      >
                        edit
                      </v-icon>
                    </template>
                    <span>{{ $t('actions.editBreak') }}</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="segmentTimePair.pairType === 'BREAK'"
                    top :disabled="isMobile()"
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        v-on="on"
                        :class="actionIcon"
                        @click="removeTimePair(segmentTimePair,item)"
                      >
                        delete
                      </v-icon>
                    </template>
                    <span>Delete {{ capitalize(segmentTimePair.pairType) }}</span>
                  </v-tooltip>
                </div>
                <div v-else class="font-italic text--disabled">
                  {{ $t('misc.unavailable') }}
                </div>
              </template>
            </SegmentTimePairTable>
          </v-card>
          <v-card tile v-if="isWorkScheduleEmployeeViewFeatureEnabled">
            <WorkScheduleCard :time-pair="item" v-if="item.startingWorkScheduleDay != null"/>
          </v-card>
        </td>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import map from 'lodash/map';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';

import constants from '@/constants';
import EventBus from '@/event-bus';

import DateTimeUtil from '@/util/date-time-util';

import displayAdaptable from '@/mixins/displayAdaptable';
import getExpectedDuration from '@/mixins/getExpectedDuration';
import getTableFooterProps from '@/mixins/getTableFooterProps';
import getTimePairStatusIndicator from '@/mixins/timepairs/getTimePairStatusIndicator';
import getWorkScheduleDeviation from '@/mixins/getWorkScheduleDeviation';
import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import hasActionIcon from '@/mixins/hasActionIcon';
import hasAppDialog from '@/mixins/hasAppDialog';
import hasAppLoader from '@/mixins/hasAppLoader';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';
import showTimePairDeviation from '@/mixins/showTimePairDeviation';
import hasWorkScheduleFeatures from '@/mixins/hasWorkScheduleFeatures';
import hasLocationFeature from '@/mixins/location/hasLocationFeature';
import hasLocationData from '@/mixins/location/hasLocationData';

import TimePairDialog from '@/components/timepairs/TimePairDialog.vue';
import TimePairDetailsDialog from '@/components/timepairs/TimePairDetailsDialog.vue';
import TimePairStatusIndicator from '@/components/timepairs/TimePairStatusIndicator.vue';
import SegmentTimePairTable from '@/components/timepairs/SegmentTimePairTable.vue';
import SegmentTimePairTableDialog from '@/components/timepairs/SegmentTimePairTableDialog.vue';
import StartDateTableCell from '@/components/timepairs/shared/StartDateTableCell.vue';
import StartTimeTableCell from '@/components/timepairs/shared/StartTimeTableCell.vue';
import EndTimeTableCell from '@/components/timepairs/shared/EndTimeTableCell.vue';
import ApprovalStatusTableCell from '@/components/timepairs/shared/ApprovalStatusTableCell.vue';
import WorkScheduleCard from '@/components/timepairs/WorkScheduleCard.vue';
import TimePairMapDialog from '@/components/timepairs/TimePairMapDialog.vue';
import ResponseUtil from '@/util/response-util';
import hasAssignmentFeatures from '@/mixins/clocking/hasAssignmentFeatures';

export default {
  name: 'PayCycleTable',

  components: {
    ApprovalStatusTableCell,
    StartDateTableCell,
    StartTimeTableCell,
    EndTimeTableCell,
    SegmentTimePairTableDialog,
    SegmentTimePairTable,
    TimePairStatusIndicator,
    TimePairDetailsDialog,
    TimePairDialog,
    WorkScheduleCard,
    TimePairMapDialog,
  },

  mixins: [
    displayAdaptable,
    getExpectedDuration,
    getTableFooterProps,
    getTimePairStatusIndicator,
    hasWorkScheduleFeatures,
    getWorkScheduleDeviation,
    hasActionIcon,
    hasAppDialog,
    hasAppLoader,
    hasAppSnackbar,
    hasTimePairFeatures,
    hasAssignmentFeatures,
    showTimePairDeviation,
    hasLocationFeature,
    hasLocationData,
  ],

  props: {
    timePairs: {
      type: Array,
      required: true,
    },
    header: {
      type: String,
      required: true,
    },
    actionable: {
      type: Boolean,
      required: true,
    },
    timePairsLoading: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      viewAdjustedTime: false,
      sortBy: constants.DataTableHeaderValues.CLOCK_IN,
      sortDesc: true,
      tableHeaders: [
        {
          text: '',
          value: 'data-table-expand',
          align: 'start',
          sortable: false,
          width: '70px',
          inactive: () => this.isMobile(),
        },
        {
          text: `${this.$t('tableHeaders.employeeId')}`,
          value: constants.DataTableHeaderValues.EMPLOYEE_ID,
          width: '110px',
          sortable: false,
        },
        {
          text: this.$t('tableHeaders.info'),
          value: constants.DataTableHeaderValues.INFO,
          align: 'start',
          width: '110px',
          sortable: false,
        },
        {
          text: this.$t('tableHeaders.position'),
          value: constants.DataTableHeaderValues.POSITION,
          inactive: () => !this.isConcurrentEmploymentActive,
        },
        {
          text: this.$t('tableHeaders.startDate'),
          value: constants.DataTableHeaderValues.START_DATE,
        },
        {
          text: this.$t('clock.clockIn'),
          value: constants.DataTableHeaderValues.CLOCK_IN,
        },
        {
          text: this.$t('clock.clockOut'),
          value: constants.DataTableHeaderValues.CLOCK_OUT,
          width: this.getClockOutColumnWidth(),
        },
        {
          text: this.$t('tableHeaders.absenceType'),
          value: constants.DataTableHeaderValues.ABSENCE_TYPE,
          align: 'center',
          width: this.getStatusOrAbsenceColumnWidth(),
          inactive: () => this.isTimePairApprovalFeatureActive,
        },
        {
          text: this.$t('tableHeaders.duration'),
          value: constants.DataTableHeaderValues.DURATION,
          align: 'center',
        },
        {
          text: this.$t('tableHeaders.expectedDuration'),
          value: constants.DataTableHeaderValues.EXPECTED_DURATION,
          align: 'center',
          sortable: false,
          inactive: () => !this.isWorkScheduleEmployeeViewFeatureEnabled,
        },
        {
          text: this.$t('tableHeaders.status'),
          value: constants.DataTableHeaderValues.STATUS,
          align: 'center',
          width: this.getStatusOrAbsenceColumnWidth(),
          inactive: () => !this.isTimePairApprovalFeatureActive,
        },
        {
          text: this.$t('tableHeaders.actions'),
          value: constants.DataTableHeaderValues.ACTIONS,
          align: 'center',
          sortable: false,
          width: this.isMenuDrawerClosable() ? '280px' : '300px',
        },
        {
          text: `${this.$t('tableHeaders.clone')} ${this.isMobile() ? '' : '*'}`,
          value: constants.DataTableHeaderValues.CLONE,
          align: 'end',
          sortable: false,
          width: '70px',
        },
        {
          text: '',
          value: 'data-table-expand',
          align: 'start',
          sortable: false,
          width: '70px',
          inactive: () => !this.isMobile(),
        },
      ],
      segmentTimeTableHeaders: [
        {
          text: this.$t('tableHeaders.info'),
          value: constants.DataTableHeaderValues.INFO,
        },
        {
          text: this.$t('tableHeaders.startDate'),
          value: constants.DataTableHeaderValues.START_DATE,
        },
        {
          text: this.$t('clock.startTime'),
          value: constants.DataTableHeaderValues.CLOCK_IN,
        },
        {
          text: this.$t('clock.endTime'),
          value: constants.DataTableHeaderValues.CLOCK_OUT,
        },
        {
          text: this.$t('tableHeaders.duration'),
          value: constants.DataTableHeaderValues.DURATION,
        },
        {
          text: this.$t('tableHeaders.actions'),
          value: constants.DataTableHeaderValues.ACTIONS,
          align: 'center',
        },
      ],
      itemsPerPage: 10,
      expanded: [],
      editingTimePair: {},
      editShiftTimePair: {},
      shiftTimePair: {},
      segmentTimePairAddingDialog: false,
      viewingTimePair: {},
      viewingSegmentTimePairs: [],
      viewingSegmentTimePairsShift: {},
      timePairEditDialog: false,
      timePairViewDialog: false,
      segmentTimePairViewDialog: false,
      notificationTimePairId: null,
      notificationType: null,
      timePairMapDialogActive: false,
    };
  },

  computed: {
    ...mapState(['activeTimePair', 'notifications', 'employee']),
    ...mapGetters(['getWorkScheduleDayWorkingMinutes']),

    duration() {
      let totalDuration = 0;

      forEach(filter(this.timePairs, (timePair) => !timePair.isInactive), (timePair) => {
        if (timePair.absenceType == null) {
          totalDuration += timePair.roundedDuration;
        }
      });

      return DateTimeUtil.getFormattedDuration(totalDuration);
    },

    activeHeaders() {
      return filter(this.tableHeaders, (header) => (header.inactive ? !header.inactive() : true));
    },

    roundingEnabled() {
      let rounding = false;
      this.timePairs.forEach((timePair) => {
        if (!rounding && timePair.rounding) {
          rounding = timePair.rounding;
        }
      });

      return rounding;
    },
  },

  watch: {
    timePairsLoading() {
      this.displayNotificationTimePair();
    },
  },

  methods: {
    ...mapActions(['deleteTimePair']),

    getClockOutColumnWidth() {
      if (this.isXlViewport()) return '';

      if (this.isMenuDrawerClosable()) return '120px';

      return '190px';
    },

    getStatusOrAbsenceColumnWidth() {
      if (this.isXlViewport()) return '';

      if (this.isMenuDrawerClosable()) return '160px';

      return '210px';
    },

    capitalize(string) {
      return capitalize(string);
    },

    formatDuration(timePair) {
      return DateTimeUtil.getFormattedDuration(timePair.roundedDuration);
    },

    getRowIndicators(timePair) {
      const indicators = this.getTimePairClockingTypeIndicators(timePair);

      if (!isEmpty(timePair.segmentTimePairs)) {
        if (find(timePair.segmentTimePairs, (seg) => seg.pairType === constants.WorkSchedule.SegmentType.BREAK)) {
          indicators.push(this.getIndicatorObject(
            'shift_breaks', 'emoji_food_beverage', constants.AppColors.PRIMARY, this.$t('indicators.containsBreaks'),
          ));
        }
        if (find(timePair.segmentTimePairs, (seg) => seg.pairType !== constants.WorkSchedule.SegmentType.BREAK)) {
          indicators.push(this.getIndicatorObject(
            'shift_overtime', 'alarm', constants.AppColors.PRIMARY, this.$t('indicators.containsOvertime'),
          ));
        }
      } else if (timePair.pairType !== 'SHIFT') {
        indicators.push(this.getIndicatorObject(
          'shift_overtime', 'alarm', constants.AppColors.PRIMARY, this.$t('indicators.containsOvertime'),
        ));
      }

      if (timePair.isInactive) {
        indicators.push(this.getIndicatorObject(
          'deleted',
          'delete',
          constants.AppColors.ERROR,
          timePair.deletedDate
            ? this.$t('indicators.timeDeletedOn',
              { time: DateTimeUtil.convertToHumanReadableDate(timePair.deletedDate) })
            : this.$t('indicators.pendingDelete'),
        ));
      }

      if (timePair.isAbsence) {
        indicators.push(this.getIndicatorObject(
          'absent',
          'alarm_off',
          constants.AppColors.WARN,
          this.$t('indicators.employeeAbsence', { type: timePair.absenceType }),
        ));
      }

      return indicators;
    },

    getItemClass(item) {
      return item.isAbsence ? 'absenceRow' : '';
    },

    editTimePair(timePair, shiftTimePair) {
      if (this.segmentTimePairViewDialog) this.segmentTimePairViewDialog = false;

      this.editingTimePair = timePair;
      this.editShiftTimePair = shiftTimePair || {};
      this.timePairEditDialog = true;
    },

    closeEditTimePair() {
      this.editingTimePair = {};
      this.editShiftTimePair = {};
      this.timePairEditDialog = false;
    },

    addSegmentTimePair(shiftTimePair) {
      if (this.segmentTimePairViewDialog) this.segmentTimePairViewDialog = false;

      this.shiftTimePair = shiftTimePair;
      this.segmentTimePairAddingDialog = true;
    },

    closeAddSegmentTimePair() {
      this.shiftTimePair = {};
      this.segmentTimePairAddingDialog = false;
    },

    viewTimePair(timePair) {
      this.viewingTimePair = timePair;
      this.timePairViewDialog = true;
    },

    closeViewTimePair() {
      this.viewingTimePair = {};
      this.timePairViewDialog = false;
    },

    cloneShift(timePair) {
      this.showAppConfirmDialog({
        message: this.$t('dialogs.confirm.startShiftWithSettings'),
        confirmText: this.$t('clock.start'),
        confirmHandler: () => this.confirmPostClonedTime(timePair),
      });
    },

    confirmPostClonedTime(timePair) {
      this.showAppLoader(this.$t('appLoader.cloning'));
      EventBus.$emit('clock:create-clock-in', {
        timePairCustomFieldValues: timePair.timePairCustomFieldValues,
        selectedAssignment: timePair.employeeAssignment,
        isCloningTimePair: true,
        lastly: () => {
          this.hideAppLoader();
        },
      });
    },

    removeTimePair(timePair, shiftTimePair) {
      this.showAppConfirmDialog({
        message: this.$t('dialogs.confirm.deleteTime'),
        color: constants.AppColors.ERROR,
        confirmText: this.$t('actions.delete'),
        confirmHandler: () => this.confirmRemoveTimePair(timePair, shiftTimePair),
      });
    },

    confirmRemoveTimePair(timePair, shiftTimePair) {
      this.showAppLoader(this.$t('appLoader.deletingTime'));
      const notificationIds = map(
        filter(
          this.notifications,
          (notification) => notification.timePairId === timePair.id
            || notification.timePairId === timePair.originalVersionId,
        ),
        (tp) => tp.notificationId,
      );
      this.deleteTimePair({
        timePair,
        shiftTimePair: shiftTimePair || timePair,
        notificationIds,
      })
      .then(() => this.showSnackbar({
        text: this.$t('snackbar.deleteSuccess'),
        color: constants.AppColors.ERROR,
      }))
      .catch((error) => {
        this.showAppErrorDialog({
          message: ResponseUtil.getTimeRequestErrorMessage(error.response, timePair, false),
        });
      })
      .finally(() => {
        this.hideAppLoader();
      });
    },

    viewSegmentTimePairs(shiftTimePair) {
      this.viewingSegmentTimePairsShift = shiftTimePair;
      this.viewingSegmentTimePairs = shiftTimePair.segmentTimePairs;
      this.segmentTimePairViewDialog = true;
    },

    closeViewSegmentTimePairs() {
      this.viewingSegmentTimePairsShift = {};
      this.viewingSegmentTimePairs = [];
      this.segmentTimePairViewDialog = false;
    },

    customPayCycleTableSort(items, index, isDesc) {
      if (isDesc == null || items.length === 0 || index.length === 0 || isDesc.length === 0) return items;

      switch (index[0]) {
        case constants.DataTableHeaderValues.START_DATE:
        case constants.DataTableHeaderValues.CLOCK_IN:
        case constants.DataTableHeaderValues.CLOCK_OUT: {
          return this.sortByDateText(items, isDesc[0], index[0]);
        }
        case constants.DataTableHeaderValues.DURATION:
        case constants.DataTableHeaderValues.STATUS: {
          return this.valueSort(items, isDesc[0], index[0]);
        }
        default:
          return items;
      }
    },

    sortByDateText(items, isDesc, key) {
      if (isDesc) {
        return items.sort(
          (timePairA, timePairB) => DateTimeUtil.dateTimeIsGreater(
            timePairA[key].date ? timePairA[key].date : timePairA[key],
            timePairB[key].date ? timePairB[key].date : timePairB[key],
          ),
        );
      }

      return items.sort(
        (timePairA, timePairB) => DateTimeUtil.dateTimeIsGreater(
          timePairB[key].date ? timePairB[key].date : timePairB[key],
          timePairA[key].date ? timePairA[key].date : timePairA[key],
        ),
      );
    },

    valueSort(items, isDesc, key) {
      const sortedItems = items.sort((timePairA, timePairB) => {
        if (timePairA[key] > timePairB[key]) return 1;
        if (timePairA[key] < timePairB[key]) return -1;
        return 0;
      });
      if (isDesc) {
        return sortedItems;
      }

      return sortedItems.reverse();
    },

    displayNotificationTimePair() {
      const timePair = find(this.timePairs, ['id', this.notificationTimePairId]);

      if (!this.timePairsLoading && this.notificationTimePairId && timePair) {
        this.notificationTimePairId = null;

        if (this.notificationType === constants.Notifications.NotificationTypeId.MANAGER_REJECTED) {
          this.editTimePair(timePair);
        } else {
          this.viewTimePair(timePair);
        }
      }
    },

    getTimePairsOrAdjustedTimePairs() {
      if (this.viewAdjustedTime === true) {
        let adjustedPairs = map(this.timePairs.filter((it) => it.absenceType === null),
          (pair) => pair.adjustedVersion);

        adjustedPairs = adjustedPairs.filter(
          (it) => it !== null,
        ).concat(this.timePairs.filter((it) => it.absenceType !== null));

        return adjustedPairs;
      }

      return this.timePairs;
    },

    updateTableHeaders() {
      this.tableHeaders.find(
        (tableHeader) => tableHeader.value === constants.DataTableHeaderValues.DURATION,
      ).text = this.viewAdjustedTime ? this.$t('tableHeaders.adjustedDuration') : this.$t('tableHeaders.duration');

      if (!this.isCloneTimeActive) {
        this.tableHeaders = this.tableHeaders.filter(
          (tableHeader) => tableHeader.value !== constants.DataTableHeaderValues.CLONE,
        );
      }
    },

    viewTimePairMap(timePair) {
      this.viewingTimePair = timePair;
      this.timePairMapDialogActive = true;
    },
  },

  mounted() {
    EventBus.$on('time-pairs:notification-action', (payload) => {
      this.notificationTimePairId = payload.timePairId;
      this.notificationType = payload.type;
      this.displayNotificationTimePair();
    });
    this.updateTableHeaders();
  },
};
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
>>> .v-data-table__expanded.v-data-table__expanded__content {
  background-color: #f2f2f2;
  box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
  -webkit-box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
  -moz-box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
}

/*noinspection CssUnusedSymbol*/
>>> .absenceRow {
  background-color: lightgray;
}
</style>
